import { createListenerMiddleware } from '@reduxjs/toolkit';
import Analytics from '../analytics';
import { setApiAuthToken, removeApiAuthToken } from '../api/api-helper';
import { clearInviteRsvpStatuses, setInviteRsvpStatusForToken } from './inviteRsvpSlice';
import { markReservationsChangedThunk } from './userSlice';
import {
  postBookingBookThunk,
  postBookingDetailsThunk,
  postGoogleBookingDetailsThunk,
  setBookingFlowState,
  setDetails,
  setVenueName,
  markAddToCartTracked,
  getBookingIdentity,
} from './bookingSlice';
import bookingFlowSideEffects from './helpers/bookingFlowSideEffects';
import { setSearchParams } from '../utils/searchParamUtils';
import {
  ALL_DAY, CORE_DATE_PARAM, CORE_SEATS_PARAM, CORE_TIME_PARAM,
} from '../constants/resyCore';

import {
  MODAL_MODES,
  getMultipleLists,
  getMultipleListsByVenue,
  editListThunk,
  resetModalErrorState,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
} from './favoriteSlice';

import { checkIfWidget, Logger } from '../utils';
import { getLocationPath, getLocationUrl, getSearchParamsAsObject } from '../utils/locationUtils';

const $locationReadOnlyShim = {
  path: getLocationPath,
  url: getLocationUrl,
  search: getSearchParamsAsObject,
};

const listenerMiddleware = createListenerMiddleware();

const getVenueNameForCart = (booking) => {
  const reservationVenue = booking.reservation?.event?.venue || booking.reservation?.venue;
  return booking.reservation?.venueName
    || (typeof reservationVenue === 'string' ? reservationVenue : reservationVenue?.name)
    || booking.details?.venue?.name
    || booking.venueName;
};

const trackCartIfReady = ({ getState, dispatch }) => {
  const { booking } = getState();
  const bookingKey = getBookingIdentity(booking);
  const venueName = getVenueNameForCart(booking);
  if (!bookingKey
      || booking.detailsBookingKey !== bookingKey
      || booking.cartTrackedBookingKey === bookingKey
      || !venueName) return;

  Analytics().setDetails({ ...booking, venueName });
  dispatch(markAddToCartTracked(bookingKey));
};

listenerMiddleware.startListening({
  actionCreator: setDetails,
  effect: (action, listenerApi) => trackCartIfReady(listenerApi),
});

// Direct URL and RWG entries can load details before venue metadata.
listenerMiddleware.startListening({
  actionCreator: setVenueName,
  effect: (action, listenerApi) => trackCartIfReady(listenerApi),
});

listenerMiddleware.startListening({
  actionCreator: setBookingFlowState,
  effect: (action, { getState }) => {
    // you need to pass the token along if this is navigating to confirmation
    // so you can set it in search
    bookingFlowSideEffects(getState());
  },
});

listenerMiddleware.startListening({
  actionCreator: postBookingBookThunk.fulfilled,
  effect: (action, { dispatch, getState }) => {
    // Concierge bookings change the guest's reservations, not the logged-in user's
    const affectsImmediateUserReservationCounts = !getState().booking?.bookOnBehalfOf;
    if (affectsImmediateUserReservationCounts) {
      // When this runs in the widget, the timestamp lands in the widget origin's localStorage, which
      // resy.com can't read; resy.com records the change itself when it receives the postMessage below.
      // So this call probably isn't important on the widget side; we're including it for
      // consistency's sake and to avoid leaving a trap for future development (e.g. if the widget ever
      // shows a reservation count).
      dispatch(markReservationsChangedThunk());
    }

    /**
      * resy.com won't know that the reservation was cancelled without a refresh,
      * so this is how we will tell it
    */
    const message = { event: 'onResyReservationsSync', affectsImmediateUserReservationCounts };
    if (window.opener) {
      window.opener.postMessage(message, '*');
    } else {
      window.parent.postMessage(message, '*');
    }
  },
});

// Accepting or declining an invite changes the user's reservations
listenerMiddleware.startListening({
  actionCreator: setInviteRsvpStatusForToken,
  effect: (action, { dispatch }) => {
    dispatch(markReservationsChangedThunk());
  },
});

listenerMiddleware.startListening({
  actionCreator: editListThunk.fulfilled,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(getMultipleLists());
  },
});

listenerMiddleware.startListening({
  actionCreator: setMultiStepModalMode,
  effect: (action, listenerApi) => {
    if (action.payload === MODAL_MODES.ADD_TO_LIST) {
      const state = listenerApi.getState();
      const venueId = state.favorite.multiStepModalVenueId;
      listenerApi.dispatch(getMultipleListsByVenue(venueId));
    }
  },
});

listenerMiddleware.startListening({
  actionCreator: setMultiStepModalOpenState,
  effect: (action, listenerApi) => {
    if (action.payload === false) {
      listenerApi.dispatch(setMultiStepModalVenueId());
      listenerApi.dispatch(setMultiStepModalVenueContext());
    }
  },
});

listenerMiddleware
  .startListening({
    type: 'resyCore/setSelectedCoreISODate',
    effect: (action, { getState }) => {
      const { resyCore, resyRouter } = getState();
      const { metadataPageTitle, metadataTitle, pageName } = resyRouter;
      const $rootScopeMetadataShim = {
        title: metadataPageTitle || metadataTitle,
        pageName,
      };
      if (resyCore.didCoreISODateUpdate) {
        setSearchParams(CORE_DATE_PARAM, resyCore.selectedCoreISODate);
        if (checkIfWidget()) {
          Analytics().page($locationReadOnlyShim, document.referrer, $rootScopeMetadataShim);
        }
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'resyCore/setSelectedCoreSeats',
    effect: (action, { getState }) => {
      const { resyCore, resyRouter } = getState();
      const { metadataPageTitle, metadataTitle, pageName } = resyRouter;
      const $rootScopeMetadataShim = {
        title: metadataPageTitle || metadataTitle,
        pageName,
      };
      if (resyCore.didCoreSeatsUpdate) {
        setSearchParams(CORE_SEATS_PARAM, resyCore.selectedCoreSeats);
        if (checkIfWidget()) {
          Analytics().page($locationReadOnlyShim, document.referrer, $rootScopeMetadataShim);
        }
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'resyCore/setSelectedCoreTime',
    effect: (action, { getState }) => {
      const { resyCore } = getState();
      if (resyCore.didCoreTimeUpdate) {
        const selectedCoreTime = resyCore.selectedCoreTime === ALL_DAY ? null : resyCore.selectedCoreTime;
        setSearchParams(CORE_TIME_PARAM, selectedCoreTime);
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'authToken/setAuthToken',
    effect: (action, listenerApi) => {
      const token = action?.payload;
      if (token) {
        setApiAuthToken(token);
      }
      listenerApi.dispatch(clearInviteRsvpStatuses());
    },
  });

listenerMiddleware.startListening({
  type: 'authToken/removeAuthToken',
  effect: (action, listenerApi) => {
    removeApiAuthToken();
    listenerApi.dispatch(clearInviteRsvpStatuses());
  },
});

listenerMiddleware
  .startListening({
    type: 'favorite/setErrorModalOpenState',
    effect: (action, listenerApi) => {
      if (action.payload === false) {
        listenerApi.dispatch(resetModalErrorState());
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'user/setUserData',
    effect: (action) => {
      const userData = action.payload;
      Analytics().setUser(userData);

      // Set RUM user identity for experiment metric attribution.
      if (typeof window !== 'undefined' && window.DD_RUM?.setUser && userData?.id) {
        window.DD_RUM.setUser({ id: String(userData.id) });
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'user/resetUserData',
    effect: () => {
      if (typeof window !== 'undefined' && window.DD_RUM?.clearUser) {
        window.DD_RUM.clearUser();
      }
    },
  });

listenerMiddleware
  .startListening({
    actionCreator: postBookingDetailsThunk.rejected,
    effect: (action) => {
      Logger.error(action.payload);
    },
  });

listenerMiddleware
  .startListening({
    actionCreator: postGoogleBookingDetailsThunk.rejected,
    effect: (action) => {
      Logger.error(action.payload);
    },
  });

listenerMiddleware
  .startListening({
    actionCreator: postBookingBookThunk.rejected,
    effect: (action) => {
      Logger.error(action.payload);
    },
  });

export default listenerMiddleware;
